import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  FormControlLabel,
  Paper,
  TextField,
  IconButton,
  AppBar,
  Toolbar,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

import SupportingFuncs from "./SupportingFuncs";

///CSV Parse
import { unparse } from "papaparse";

function App() {
  const [user, setUser] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [rvtFiles, setRvtFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [processingList, setProcessingList] = useState([]);

  //for progress bar
  const [processing, setProcessing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);

  //input for parameters
  const [projectParamInput, setProjectParamInput] = useState(
    "Project Number, Project Name, Project Address, Project Status"
  );
  const [elementParamInput, setElementParamInput] = useState(
    "Type Name, Type Mark, Type Comments, Volume"
  );

  const REACT_APP_FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;
  const parseParams = (input) =>
    input
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const apiBaseURL = process.env.REACT_APP_API_URL; // "https://localhost:44352/"; //https://api.acc.digi-ants.com";
  axios.defaults.baseURL = apiBaseURL;
  axios.defaults.withCredentials = true;
  //axios.defaults.headers.common["Content-Type"] = "application/json"; //No need to set this

  useEffect(() => {
    axios
      .get("/api/user/profile")
      .then((res) => setUser(res.data))
      .catch(console.error);
  }, []);

  const login = () => {
    window.location.href =
      apiBaseURL + "/api/auth/login?callbackurl=" + REACT_APP_FRONTEND_URL;
    //"https://extractor.acc.digi-ants.com";
  };

  const loadHubs = async () => {
    const res = await axios.get("/api/hubs");
    const hubs = res.data.data.map((hub) => ({
      id: hub.id,
      name: hub.attributes.name,
      type: "hub",
      children: [],
      isLoaded: false,
    }));
    console.log("Hubs data:", hubs);
    setTreeData(hubs);
  };

  const loadProjects = async (hubNode) => {
    console.log("Loading projects for hub:", hubNode);
    console.log(`/api/projects/${hubNode.id}`);
    const res = await axios.get(`/api/projects/${hubNode.id}`, {
      withCredentials: true,
    });
    hubNode.children = res.data.data
      .sort((a, b) => (a.attributes.name > b.attributes.name ? 1 : -1))
      .map((project) => ({
        id: project.id,
        name: project.attributes.name,
        hubId: hubNode.id,
        type: "project",
        children: [],
        isLoaded: false,
      }));
    hubNode.isLoaded = true;
    setTreeData([...treeData]);
  };

  const loadFolders = async (projectNode) => {
    const res = await axios.get(
      `/api/folders/${projectNode.hubId}/${projectNode.id}/topFolders`
    );
    projectNode.children = res.data.data
      .sort((a, b) => (a.attributes.name > b.attributes.name ? 1 : -1))
      .map((folder) => ({
        id: folder.id,
        name: folder.attributes.name,
        projectId: projectNode.id,
        type: "folder",
        children: [],
        isLoaded: false,
      }));
    projectNode.isLoaded = true;
    setTreeData([...treeData]);
  };

  const loadFolderContents = async (folderNode) => {
    if (folderNode.isLoaded) {
      setRvtFiles([...folderNode.rvtFiles]);
      setSelectedFiles([]);
      return;
    }
    const res = await axios.get(
      `/api/folder/contents/${folderNode.projectId}/${encodeURIComponent(
        folderNode.id
      )}`
    );
    const contents = res.data.data;

    folderNode.children = contents
      .filter((item) => item.type === "folders")
      .sort((a, b) => (a.attributes.name > b.attributes.name ? 1 : -1))
      .map((item) => ({
        id: item.id,
        name: item.attributes.name,
        projectId: folderNode.projectId,
        type: "folder",
        children: [],
        isLoaded: false,
      }));

    folderNode.isLoaded = true;

    const rvtFiles = contents
      .filter(
        (item) =>
          item.type === "items" && item.attributes.displayName.endsWith(".rvt")
      )
      .sort((a, b) =>
        a.attributes.displayName > b.attributes.displayName ? 1 : -1
      );

    folderNode.rvtFiles = rvtFiles;

    setRvtFiles([...folderNode.rvtFiles]);
    setSelectedFiles([]);

    setTreeData([...treeData]);
  };

  const handleNodeClick = async (node) => {
    if (!node.isLoaded) {
      if (node.type === "hub") await loadProjects(node);
      else if (node.type === "project") await loadFolders(node);
      else if (node.type === "folder") await loadFolderContents(node);
    } else if (node.type === "folder") {
      await loadFolderContents(node);
    }
  };

  const renderTree = (nodes) => {
    // console.log("Rendering tree nodes:", nodes);
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return null;

    return nodes.map((node) => (
      <TreeItem
        id={node.id}
        itemId={node.id}
        label={node.name}
        onClick={(e) => {
          handleNodeClick(node);
          e.stopPropagation();
        }}
      >
        {Array.isArray(node.children) && node.children.length > 0
          ? renderTree(node.children)
          : null}
      </TreeItem>
    ));
  };

  const toggleFileSelection = (file) => {
    setSelectedFiles((prev) =>
      prev.includes(file) ? prev.filter((f) => f !== file) : [...prev, file]
    );
  };

  const addToProcessingList = () => {
    const unique = selectedFiles.filter(
      (file) => !processingList.some((f) => f.id === file.id)
    );
    setProcessingList([...processingList, ...unique]);
    setSelectedFiles([]);
  };

  const removeFromProcessingList = (file) => {
    setProcessingList((prev) => prev.filter((f) => f !== file));
  };

  ///processing one file
  /**
   * Process a file by fetching its details, metadata, and object tree.
   * @param {*} file
   */
  const processFile = async (file) => {
    try {
      const projectId = SupportingFuncs.projectIDFromURL(file.links.self.href);
      const fileDetailsRes = await axios.get(
        `/api/items/details/${projectId}/${file.id}`
      );
      console.log("File details:", fileDetailsRes.data);
      const revitFilename = fileDetailsRes.data.data.attributes.displayName;
      console.log("Processing file:", revitFilename);

      //this is already the base64 encoded URN
      const modelDerivativeURN =
        fileDetailsRes.data.included[0].relationships.derivatives.data.id;
      //console.log("Model Derivative URN:", modelDerivativeURN);

      const metadataRes = await axios.get(
        `/api/models/metadata/${modelDerivativeURN}`
      );
      //console.log("Metadata: ", metadataRes.data);

      //get the master view (the metadata)
      const masterView = metadataRes.data.data.metadata.find(
        (view) => true // view?.isMasterView === true
      );
      //console.log("Master View:", masterView);

      //Get the object tree from master view
      const objectTreeRes = await axios.get(
        `/api/models/objectTree/${modelDerivativeURN}/${masterView?.guid}`
      );
      //console.log("Object Tree:", objectTreeRes.data);

      const elementList = [];
      //root model object
      const rootModelObject = objectTreeRes.data.data.objects[0];

      //recursively get the children of the root model object
      const recursiveGetChildren = (currentNode, currentLevel) => {
        if (currentLevel >= 3 || !currentNode.objects) {
          elementList.push(currentNode);
          if (!currentNode.objects) return;
        }
        const nextLevel = currentLevel + 1;
        for (const child of currentNode.objects) {
          child.parent = currentNode;
          child.level = nextLevel;
          recursiveGetChildren(child, nextLevel);
        }
      };

      for (const object of rootModelObject.objects) {
        object.level = 0;
        recursiveGetChildren(object, 0);
      }

      //console.log("Element List:", elementList);

      //Get the properties of the master view
      //Get the object tree from master view
      const objectPropertiesRes = await axios.get(
        `/api/models/objectProperties/${modelDerivativeURN}/${
          masterView?.guid
        }/${58}`
      );
      //console.log("Object Props:", objectPropertiesRes.data);

      //Turn to properties dictionary
      const lookupPropsById = Object.fromEntries(
        objectPropertiesRes.data.data.collection.map((item) => [
          item.objectid,
          item,
        ])
      );
      //console.log("Lookup Props By ID:", lookupPropsById);

      //assign properties for the root model object
      rootModelObject.properties =
        lookupPropsById[rootModelObject.objectid].properties;
      rootModelObject.filename = revitFilename;

      //console.log("Root Model Object with Props:", rootModelObject);

      //Get the properties of object by id
      for (const element of elementList) {
        element.filename = revitFilename;

        const objectProps = lookupPropsById[element.objectid];
        if (objectProps) {
          element.properties = objectProps.properties;
        } else {
          console.log("No properties found for object:", element.objectid);
        }
      }

      const projectParametersList = parseParams(projectParamInput);
      const elementParametersList = parseParams(elementParamInput);
      /**
       * From the element and list of parameters, extract the NEW element with the properties in the parameters list
       * @param {*} element
       * @param {*} parameters
       * @returns
       */
      const extractElementWithProps = (element, parameters) => {
        const newElement = {
          _id: element.objectid,
          _name: element.name,
          _filename: element.filename,
        };

        /**
         * Recersively get the property value from the element
         * @param {*} propertiesObj
         * @param {*} propertyName
         * @returns the property object {name,value}; null is not found
         */
        const getProperty = (propertiesObj, propertyName) => {
          for (const key in propertiesObj) {
            const value = propertiesObj[key];
            const isObject =
              value !== null &&
              typeof value === "object" &&
              !Array.isArray(value);

            if (isObject) {
              const foundProp = getProperty(value, propertyName);
              if (foundProp) {
                return { name: propertyName, value: foundProp.value };
              }
            } else {
              if (key === propertyName) {
                return { name: propertyName, value: value };
              }
            }
          }
          return null;
        };

        for (const prop of parameters) {
          //Get propperty from element's properties
          const propValue = getProperty(element.properties, prop);
          if (propValue) {
            newElement[prop] = propValue.value; //assing if has property
          } else {
            newElement[prop] = "[[n/a]]"; //if not property
          }
        }

        return newElement;
      };

      const newProjectWithProps = extractElementWithProps(
        rootModelObject,
        projectParametersList
      );
      const newElementWithPropsList = elementList
        .filter((element) => element.level > 2)
        .map((element) =>
          extractElementWithProps(element, elementParametersList)
        );

      console.log("newProjectWithProps:", newProjectWithProps);
      console.log("Element List with Props:", newElementWithPropsList);

      return {
        projectObject: newProjectWithProps,
        elements: newElementWithPropsList,
      };
    } catch (error) {
      console.error("Error processing file:", error);
      return null;
    }
  };

  const processAllFiles = async (filesList) => {
    const modelsList = [];
    const elementsList = [];
    let i = 0;
    for (const file of filesList) {
      setCurrentIndex(i);
      const processedData = await processFile(file);
      i++;
      if (processedData) {
        modelsList.push(processedData.projectObject);
        elementsList.push(...processedData.elements);
      }
    }

    return {
      models: modelsList,
      elements: elementsList,
    };
  };

  const downloadCSV = (modelsList, elementsList) => {
    const csvModel = unparse(modelsList);
    SupportingFuncs.downloadCSV(csvModel, "model.csv");
    const csvElements = unparse(elementsList);
    SupportingFuncs.downloadCSV(csvElements, "elements.csv");
  };

  return (
    <>
      {/* Top Banner #1976d2*/}
      <AppBar
        position="fixed"
        sx={{ backgroundColor: "#000000", zIndex: 1201 }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {/* Left side: Logo */}
          <Box display="flex" alignItems="center">
            <img
              src="InSite.png" // Adjust path to your logo
              alt="App Logo"
              style={{
                height: 50,
                marginRight: 16,
                marginTop: 3,
                marginBottom: 3,
              }}
            />
            <img
              src="logo.png" // Adjust path to your logo
              alt="App Logo"
              style={{
                height: 80,
                marginRight: 16,
                marginTop: 3,
                marginBottom: 3,
              }}
            />
            <Typography variant="h5" noWrap marginLeft={5}>
              Model Data Extractor
            </Typography>
          </Box>

          {/* Right side: Login or Welcome */}
          <Box>
            {!user ? (
              <Button color="inherit" variant="outlined" onClick={login}>
                Login with Autodesk
              </Button>
            ) : (
              <Typography variant="h6" fontWeight={400}>
                Welcome, {user.name}
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ paddingTop: "80px", marginTop: "12px" }}>
        <Box sx={{ width: "100%", px: 2 }}>
          {user && (
            <Button
              variant="contained"
              color="success"
              onClick={() => loadHubs()}
            >
              Load Hubs
            </Button>
          )}

          {/* Main Grid */}
          <Grid
            container
            spacing={2}
            mt={2}
            style={{ marginLeft: "0px", height: "900px" }}
            alignItems="stretch"
          >
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Paper
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Hub → Project → Folder Tree
                </Typography>
                <SimpleTreeView
                  sx={{ height: "100%", maxHeight: "800px", overflow: "auto" }}
                >
                  {renderTree(treeData)}
                </SimpleTreeView>
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Paper
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                }}
              >
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Revit Files (.rvt)
                </Typography>
                {rvtFiles.length === 0 ? (
                  <Typography color="text.secondary">No files found</Typography>
                ) : (
                  <List
                    sx={{
                      height: "100%",
                      maxHeight: "770px",
                      overflow: "auto",
                    }}
                  >
                    {rvtFiles.map((file) => (
                      <ListItem key={file.id} disablePadding>
                        <FormControlLabel
                          style={{
                            marginTop: "1px",
                            marginBottom: "-10px",
                            marginLeft: "-10px",
                          }}
                          control={
                            <Checkbox
                              checked={selectedFiles.includes(file)}
                              onChange={() => toggleFileSelection(file)}
                              color="primary"
                            />
                          }
                          label={file.attributes.displayName}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                {selectedFiles.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={addToProcessingList}
                    sx={{ mt: 1 }}
                  >
                    Add to Processing List →
                  </Button>
                )}
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Paper
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                }}
              >
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Parameter Selection
                </Typography>
                <TextField
                  label="Project Parameters"
                  multiline
                  fullWidth
                  minRows={3}
                  value={projectParamInput}
                  onChange={(e) => setProjectParamInput(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="Enter comma-separated or newline-separated values"
                />
                <TextField
                  label="Element Parameters"
                  multiline
                  fullWidth
                  minRows={3}
                  value={elementParamInput}
                  onChange={(e) => setElementParamInput(e.target.value)}
                  placeholder="Enter comma-separated or newline-separated values"
                />
                <Box gap={1} mt={2}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    Files Ready to Process
                  </Typography>
                  {processingList.length === 0 ? (
                    <Typography color="text.secondary">
                      No files added yet.
                    </Typography>
                  ) : (
                    <>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={async () => {
                          try {
                            setProcessing(true);
                            const data = await processAllFiles(processingList);
                            downloadCSV(data.models, data.elements);
                          } catch {
                          } finally {
                            setCurrentIndex(null);
                            setProcessing(false);
                          }
                        }}
                      >
                        Process All Files
                      </Button>
                      <List
                        sx={{
                          height: "100%",
                          maxHeight: "700px",
                          overflow: "auto",
                        }}
                      >
                        {processingList.map((file) => (
                          <ListItem key={file.id}>
                            <Paper
                              elevation={1}
                              sx={{
                                p: 1,
                                width: "100%",
                                flexDirection: "row",
                                display: "flex",
                                justifyContent: "space-between",
                                spacing: 2,
                                marginRight: "-10px",
                                marginLeft: "-10px",
                              }}
                            >
                              <ListItemText
                                sx={{ whiteSpace: "normal" }}
                                primary={
                                  <Typography
                                    variant="body1"
                                    noWrap={false}
                                    whiteSpace={"normal"}
                                  >
                                    {file.attributes.displayName}
                                  </Typography>
                                }
                              />
                              <Box display="flex" gap={1}>
                                <IconButton
                                  sx={{ marginLeft: "1", padding: "1px" }}
                                  edge="end"
                                  color="primary"
                                  aria-label="process"
                                  onClick={async () => {
                                    try {
                                      setProcessing(true);
                                      const fileIndex =
                                        processingList.indexOf(file);
                                      setCurrentIndex(fileIndex);
                                      const data = await processFile(file);
                                      downloadCSV(
                                        [data.projectObject],
                                        data.elements
                                      );
                                    } catch {
                                    } finally {
                                      setCurrentIndex(null);
                                      setProcessing(false);
                                    }
                                  }}
                                >
                                  <PlayCircleIcon />
                                </IconButton>
                                <IconButton
                                  sx={{
                                    marginLeft: "1",
                                    padding: "1px",
                                  }}
                                  color="error"
                                  aria-label="remove"
                                  onClick={() => removeFromProcessingList(file)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Paper>
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </Box>

                <Backdrop
                  sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                  }}
                  open={processing}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={2}
                  >
                    <CircularProgress color="inherit" />
                    <Typography variant="h6">
                      Processing{" "}
                      {currentIndex !== null
                        ? processingList[currentIndex].attributes.displayName
                        : ""}
                    </Typography>
                  </Box>
                </Backdrop>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}

export default App;
